<template>
  <div class="about-page min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 py-12 px-4">
    <div class="max-w-4xl mx-auto">

      <!-- Hero -->
      <header class="text-center mb-16">
        <h1 class="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
          About <span class="text-indigo-600">WordCloudStudio</span>
        </h1>
        <p class="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          The most intuitive online word cloud generator — turn your words into stunning visual art in seconds.
        </p>
      </header>

      <!-- What is WordCloudStudio -->
      <section class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">What is WordCloudStudio?</h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          WordCloudStudio is a powerful, browser-based <strong>word cloud generator</strong> that transforms
          your text, keywords, or data into beautiful visualizations. Whether you're a designer creating
          social media graphics, a teacher preparing classroom materials, a marketer analyzing customer
          feedback, or a researcher presenting text analysis results — WordCloudStudio gives you the tools
          to create professional word clouds without any software installation.
        </p>
        <p class="text-gray-700 leading-relaxed mb-4">
          Simply paste your text, upload a word-frequency list, or type your own words and values. Our
          real-time rendering engine instantly generates a customizable word cloud that you can fine-tune
          with hundreds of Google Fonts, custom shapes (masks), color schemes, and export at up to 4K
          resolution.
        </p>
        <p class="text-gray-700 leading-relaxed">
          Built with Vue 3 and ECharts, WordCloudStudio delivers a smooth, responsive experience across
          desktop, tablet, and mobile devices. No account required to start — create unlimited word clouds
          for free, with premium plans available for advanced features like custom mask shapes and
          high-resolution exports.
        </p>
      </section>

      <!-- Key Features -->
      <section class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="flex gap-3">
            <span class="text-indigo-500 text-2xl shrink-0">🎨</span>
            <div>
              <h3 class="font-semibold text-gray-900 mb-1">1700+ Google Fonts</h3>
              <p class="text-gray-600 text-sm">Choose from an extensive library of fonts to match your brand or style. Preview fonts in real-time before applying.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-2xl shrink-0">🎭</span>
            <div>
              <h3 class="font-semibold text-gray-900 mb-1">Custom Shape Masks</h3>
              <p class="text-gray-600 text-sm">Shape your word cloud into hearts, stars, animals, logos, or upload your own custom mask image.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-2xl shrink-0">🎨</span>
            <div>
              <h3 class="font-semibold text-gray-900 mb-1">Advanced Color Controls</h3>
              <p class="text-gray-600 text-sm">Apply gradient color schemes, custom palettes, or let the generator pick harmonious colors automatically.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-2xl shrink-0">📤</span>
            <div>
              <h3 class="font-semibold text-gray-900 mb-1">High-Res Export</h3>
              <p class="text-gray-600 text-sm">Export your word cloud as PNG at up to 4K resolution. Perfect for prints, posters, and presentations.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-2xl shrink-0">📱</span>
            <div>
              <h3 class="font-semibold text-gray-900 mb-1">Fully Responsive</h3>
              <p class="text-gray-600 text-sm">Create and edit word clouds on any device — desktop, tablet, or mobile — with a touch-optimized interface.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-2xl shrink-0">🌐</span>
            <div>
              <h3 class="font-semibold text-gray-900 mb-1">9 Languages</h3>
              <p class="text-gray-600 text-sm">Interface available in English, Chinese, Japanese, Korean, French, German, Italian, Spanish, and Portuguese.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Use Cases -->
      <section class="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">Use Cases</h2>
        <div class="space-y-4">
          <div class="flex gap-3">
            <span class="text-indigo-500 text-xl shrink-0">📊</span>
            <div>
              <h3 class="font-semibold text-gray-900">Content Marketing &amp; SEO</h3>
              <p class="text-gray-600 text-sm">Visualize keyword density, create engaging featured images, and make data-driven content more shareable.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-xl shrink-0">🏫</span>
            <div>
              <h3 class="font-semibold text-gray-900">Education &amp; Research</h3>
              <p class="text-gray-600 text-sm">Summarize text analysis, visualize survey responses, and create engaging classroom materials.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-xl shrink-0">🎁</span>
            <div>
              <h3 class="font-semibold text-gray-900">Personalized Gifts</h3>
              <p class="text-gray-600 text-sm">Design unique word art gifts — wedding vows, favorite lyrics, memorial tributes, company values, and more.</p>
            </div>
          </div>
          <div class="flex gap-3">
            <span class="text-indigo-500 text-xl shrink-0">🏢</span>
            <div>
              <h3 class="font-semibold text-gray-900">Business &amp; Branding</h3>
              <p class="text-gray-600 text-sm">Create brand mood boards, visualize company values, and generate eye-catching social media graphics.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Tech Stack / Footer -->
      <section class="text-center text-gray-500 text-sm">
        <p>Built with ❤️ using Vue 3, ECharts, and Element Plus.</p>
        <p class="mt-1">Copyright © {{ currentYear }} emperinter. All rights reserved.</p>
        <nav class="mt-4 space-x-4">
          <router-link to="/" class="text-indigo-600 hover:underline">Home</router-link>
          <router-link to="/price" class="text-indigo-600 hover:underline">Pricing</router-link>
          <router-link to="/service" class="text-indigo-600 hover:underline">Terms &amp; Privacy</router-link>
        </nav>
        <p class="mt-3">
          📧 <a href="mailto:emperinter@outlook.com" class="text-indigo-600 hover:underline">emperinter@outlook.com</a>
        </p>
      </section>

    </div>
  </div>
</template>

<script>
import { useSEO } from '@/composables/useSEO'

export default {
  name: 'AboutPage',
  data() {
    return {
      currentYear: new Date().getFullYear()
    }
  },
  mounted() {
    useSEO({
      title: 'About WordCloudStudio — Free Online Word Cloud Generator',
      description: 'WordCloudStudio is a powerful browser-based word cloud generator. Create stunning word art with 1700+ fonts, custom shapes, and high-res exports. No download required.',
      keywords: 'about word cloud, word cloud generator, word art creator, wordcloudstudio, free word cloud tool, text visualization',
      canonical: '/about',
      ogTitle: 'About WordCloudStudio — Free Online Word Cloud Generator',
      ogDescription: 'Learn about WordCloudStudio — the most intuitive online word cloud generator. 1700+ fonts, custom shapes, high-res exports.',
      ogImage: 'https://wordcloudstudio.com/wcdw.png',
      twitterTitle: 'About WordCloudStudio — Free Online Word Cloud Generator',
      twitterDescription: 'Learn about WordCloudStudio — the most intuitive online word cloud generator.',
      twitterImage: 'https://wordcloudstudio.com/wcdw.png'
    })
  }
}
</script>

<style scoped>
.about-page {
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}
</style>
